import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import SelectCheckOptionsModal from './SelectCheckOptionsModal';
import NewBackgroundCheckModal from './NewBackgroundCheckModal';
import PaymentModal from './PaymentModal';
import { CheckType } from '../../types';
import { useCreateInvestigationMutation } from '../../store/api/apiSlice';

interface InvestigationFlowProps {
    isOpen: boolean;
    onClose: () => void;
}

type FlowStep = 'package' | 'details' | 'payment' | 'closed';

const getReadableError = (err: any, fallback = 'Failed to create investigation') => {
    const serverMessage = err?.data?.message ?? err?.message ?? err;
    if (Array.isArray(serverMessage)) return serverMessage.join(', ');
    if (typeof serverMessage === 'string') return serverMessage;
    if (serverMessage && typeof serverMessage === 'object') {
        return serverMessage.message || serverMessage.error || JSON.stringify(serverMessage);
    }
    return fallback;
};

export default function InvestigationFlow({ isOpen, onClose }: InvestigationFlowProps) {
    const [currentStep, setCurrentStep] = useState<FlowStep>('package');
    const [checkType, setCheckType] = useState<CheckType>(CheckType.STANDARD);
    const [investigationId, setInvestigationId] = useState<string>('');
    const [creationError, setCreationError] = useState<string>('');
    const [isCreating, setIsCreating] = useState(false);

    const [createInvestigation] = useCreateInvestigationMutation();

    const handleSelectPackage = (packageId: string) => {
        const typeMap: Record<string, CheckType> = {
            standard: CheckType.STANDARD,
            enhanced: CheckType.ENHANCED,
            pro: CheckType.PRO,
            Standard: CheckType.STANDARD,
            Enhanced: CheckType.ENHANCED,
            Pro: CheckType.PRO,
        };
        setCheckType(typeMap[packageId] || CheckType.STANDARD);
        setCurrentStep('details');
    };

    const handleDetailsNext = async (details: any) => {
        setCreationError('');
        setIsCreating(true);

        try {
            const investigationData = {
                checkType,
                employeeFirstName: details.employeeFirstName.trim(),
                ...(details.employeeMiddleName && { employeeMiddleName: details.employeeMiddleName.trim() }),
                employeeLastName: details.employeeLastName.trim(),
                employeeEmail: details.employeeEmail.trim(),
                employeePhone: details.employeePhone,
                ...(details.employeeGender && { employeeGender: details.employeeGender }),
            };

            const result = await createInvestigation({
                data: investigationData,
                documents: details.documents,
                documentTypes: details.documentTypes,
            });

            if ('error' in result) {
                setCreationError(getReadableError(result.error));
                setIsCreating(false);
                return;
            }

            const id = result.data?.id;
            if (!id) {
                setCreationError('Investigation was created but no ID was returned. Please contact support.');
                setIsCreating(false);
                return;
            }

            setInvestigationId(id);
            setIsCreating(false);
            setCurrentStep('payment');
        } catch (err) {
            setCreationError(getReadableError(err));
            setIsCreating(false);
        }
    };

    const handleBack = () => {
        if (currentStep === 'payment') {
            setCurrentStep('details');
        } else if (currentStep === 'details') {
            setCurrentStep('package');
        }
    };

    const handleClose = () => {
        setCurrentStep('package');
        setCheckType(CheckType.STANDARD);
        setInvestigationId('');
        setCreationError('');
        setIsCreating(false);
        onClose();
    };

    if (!isOpen) return null;

    return (
        <>
            <SelectCheckOptionsModal
                isOpen={currentStep === 'package'}
                onClose={handleClose}
                onSelectPackage={handleSelectPackage}
            />

            <NewBackgroundCheckModal
                isOpen={currentStep === 'details'}
                onClose={handleClose}
                onNext={handleDetailsNext}
                checkType={checkType}
                externalLoading={isCreating}
                externalError={creationError}
            />

            {currentStep === 'payment' && investigationId && (
                <PaymentModal
                    isOpen
                    onClose={handleClose}
                    onBack={handleBack}
                    checkType={checkType}
                    investigationId={investigationId}
                />
            )}

            {/* Inline overlay during investigation creation (covers transition from details → payment) */}
            {isCreating && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40">
                    <div className="flex items-center gap-3 rounded-xl bg-white px-6 py-4 shadow-lg">
                        <Loader2 className="h-5 w-5 animate-spin text-gray-700" />
                        <span className="text-sm font-medium text-gray-900">Creating investigation...</span>
                    </div>
                </div>
            )}
        </>
    );
}
